import Button from 'components/base/Button';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { CustomerBreadcrumbItems } from 'data/commonData';
import { Col, Form, Row } from 'react-bootstrap';
import { CreateCustomerForm } from 'components/forms/CreateStaffForm';
import { ChangeEvent, FormEvent, useState } from 'react';
import {
  CreateCustomerDTO,
  CreateCustomerFormData
} from 'data/from-data/create-customer-form.data';
import { useMutation, useQueryClient } from 'react-query';
import { CREATE_CUSTOMER } from 'services/api/customer';
import { FormProps } from 'data/from-data/interface/form-props.interface';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useAccessToken } from 'providers/AccessTokenProvider';
import toast from 'react-hot-toast';
interface CreateCustomerProps {
  isModal?: boolean;
  onSuccess?: () => void;
}

const CreateCustomer = ({ isModal, onSuccess }: CreateCustomerProps) => {
  const [validated, setValidated] = useState<boolean>(false);
  const [createCustomerDto, setCreateCustomerDto] =
    useState<CreateCustomerFormData>(new CreateCustomerFormData());
  const [isLoading, setLoading] = useState<boolean>(false);
  const { authUser } = useAccessToken();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createCustomerAction = useMutation('createCustomer', CREATE_CUSTOMER, {
    onError: (err: any) => {
      toast.error(err.message || 'Failed to create customer');
      setLoading(false);
    },
    onSuccess: () => {
      setLoading(false);
      queryClient.invalidateQueries('get_customer');
      if (isModal && onSuccess) {
        onSuccess();
      } else {
        navigate('/home/apps/company');
      }
    },
    onMutate: () => {
      setLoading(true);
    }
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;

    if (form.checkValidity() === false) {
      event.stopPropagation();
    } else {
      return runFormSubmissionAction(event);
    }

    setValidated(true);
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setCreateCustomerDto(prevData => ({
      ...prevData,
      [name]: {
        ...prevData[name],
        value
      }
    }));
  };

  const runFormSubmissionAction = async (e: FormEvent) => {
    e.preventDefault();

    try {
      const {
        organizationName: { value: organizationName },
        address: { value: address },
        state: { value: state },
        country: { value: country },
        companyLogoUrl: { value: companyLogoUrl },
        phoneNumber: { value: phoneNumber }
      } = createCustomerDto;

      const payload: CreateCustomerDTO = {
        ...createCustomerDto,
        organizationName,
        address,
        state,
        country,
        companyLogoUrl,
        phoneNumber,
        createdBy: {
          connect: { id: (authUser as any)?.id || authUser?._id }
        } as any
      };

      await createCustomerAction.mutateAsync(payload);
      toast.success('Customer created successfully');
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to create customer. Please try again.'
      );
    }
  };

  const renderFormFields = () => {
    return Object.entries(createCustomerDto).map(([key, formProps]) => (
      <Col md="6" key={key}>
        <CreateCustomerForm
          formProps={formProps as unknown as FormProps}
          handleInputChange={handleInputChange}
        />
      </Col>
    ));
  };
  return (
    <div className={isModal ? '' : 'mb-9'}>
      {!isModal && (
        <>
          <PageBreadcrumb items={CustomerBreadcrumbItems} />
          <div className="border-bottom mb-7 mx-n3 px-2 mx-lg-n6 px-lg-6">
            <Row>
              <Col xl={9}>
                <div className="d-sm-flex justify-content-between">
                  <h2 className="mb-4">Create Company</h2>
                  <div className="d-flex mb-3">
                    <Link to="/home/apps/company">
                      <Button variant="phoenix-primary" className="me-2 px-6">
                        Cancel
                      </Button>
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </>
      )}

      <Row>
        <Col xl={isModal ? 12 : 9}>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            {!isModal && <h4 className="mb-3">Company Information</h4>}
            <Row className="g-3 mb-8">{renderFormFields()}</Row>
            <div className="text-end">
              <Button
                variant="primary"
                className="mt-6"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Creating...' : 'Create Company'}
              </Button>
            </div>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default CreateCustomer;
